import { useAtom } from "jotai";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { reportError } from "../utils/error";
export const mergeStatus = (...status) => {
    let isAnyPending = false;
    let isAnyRunning = false;
    for (const s of status) {
        if (s === "running") {
            isAnyRunning = true;
        }
        if (s === "pending") {
            isAnyPending = true;
        }
        if (s === "failed") {
            return "failed";
        }
    }
    if (isAnyRunning)
        return "running";
    if (isAnyPending)
        return "pending";
    return "complete";
};
/**
 * Run an asynchronous task sequentially and enable a consumer to track running status
 *
 * Prevents concurrent executions - ensures that previous executions are complete before re-running
 *
 * Supports automatic or on-demand invocation
 */
export default function useAsyncTask(run, opts = {}) {
    const [runState, setRunState] = useState("pending");
    const [runningP, setRunningP] = useState(null);
    const [data, setData] = opts?.atom
        ? // biome-ignore lint/correctness/useHookAtTopLevel: Conditional hook based on stable opts
            useAtom(opts.atom)
        : // biome-ignore lint/correctness/useHookAtTopLevel: Conditional hook based on stable opts
            useState(null);
    const clear = () => {
        setData(null);
    };
    const attemptRun = useCallback(run, opts.deps ?? []);
    const untrackedTrigger = async () => {
        const maxRetries = opts.retries ?? 0;
        let lastError;
        for (let attempt = 0; attempt <= maxRetries; attempt++) {
            try {
                setRunState("running");
                const res = await attemptRun();
                setData(res);
                setRunState("complete");
                return res;
            }
            catch (e) {
                lastError = e;
                if (attempt < maxRetries) {
                    // Retry on next iteration
                    continue;
                }
                // All retries exhausted
                console.error(e);
                setRunState("failed");
                throw e;
            }
            finally {
                if (attempt >= maxRetries) {
                    setRunningP(null);
                }
            }
        }
        // This line is unreachable but needed for type checking
        throw lastError;
    };
    const untrackedTriggerD = opts.debounceBy
        ? debounce(untrackedTrigger, opts.debounceBy)
        : untrackedTrigger;
    const trigger = useCallback(async () => {
        try {
            await runningP;
            const nextRunningP = untrackedTriggerD();
            setRunningP(nextRunningP ?? null);
            await nextRunningP;
        }
        finally {
            setRunningP(null);
        }
    }, [attemptRun]);
    useEffect(() => {
        if (opts?.autoRun === false)
            return;
        trigger().catch(e => {
            reportError(e, {
                subject: opts?.subject,
                action: opts?.action,
                ...opts?.errorContext,
            });
        });
    }, [attemptRun]);
    return {
        runState,
        trigger,
        data,
        clear,
    };
}
export function useLazyAsyncTask(run, opts = {}) {
    return useAsyncTask(run, {
        autoRun: false,
        ...opts,
    });
}
//# sourceMappingURL=use-async-task.js.map